import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import useKeyPress from '../../../hooks/useKeyPress';
import TextNumbers from './text-number';
import styles from './textNumber.module.css';
import ArrowRight from '../../../assets/images/arrow_right.png';
import ArrowLeft from '../../../assets/images/arrow_left.png';
import CommandsContext from '../../../context/commandsContext';
import MenuContext from '../../../context/menuContext';

function NumberView({
  caption,
  chars,
  focus,
  defaultKey1,
  defaultKey2,
  defaultKey3,
  defaultKey4,
  defaultKey5,
  defaultKey6,
  defaultKey7,
  defaultKey8,
  defaultKey9,
  defaultKey10,
  defaultKey11,
  defaultKey12,
  initiallyDisableKeypress,
  onChange,
  onSave,
  onRightHold: onParentRightHold,
  onLeftHold: onParentLeftHold,
}) {
  const [isKeypressDisabled, setIsKeypressDisabled] = useState(initiallyDisableKeypress);
  const [currentFocus, setCurrentFocus] = useState(focus);
  const [values, setValues] = useState({
    1: chars[defaultKey1].name,
    2: chars[defaultKey2].name,
    3: chars[defaultKey3].name,
    4: chars[defaultKey4].name,
    5: chars[defaultKey5].name,
    6: chars[defaultKey6].name,
    7: chars[defaultKey7].name,
    8: chars[defaultKey8].name,
    9: chars[defaultKey9].name,
    10: chars[defaultKey10].name,
    11: chars[defaultKey11].name,
    12: chars[defaultKey12].name,
  });
  const timeoutRef = useRef();
  const rightPress = useKeyPress('ArrowRight');
  const leftPress = useKeyPress('ArrowLeft');
  const min = 3;
  const limit = 12;
  const {onUpdateCommands} = useContext(CommandsContext);
  const {setDisableMenu} = useContext(MenuContext);

  const result = useMemo(
    () => Object.values(values).slice(currentFocus > 8 ? 8 : currentFocus - 1),
    [values, currentFocus],
  );

  const onLeftHold = useCallback(
    leftPressParam => {
      if ((!leftPress && leftPressParam === undefined) || leftPressParam === false) return;
      onParentLeftHold?.();
      setDisableMenu(true);
      setTimeout(() => setDisableMenu(false), 500);
    },
    [leftPress],
  );

  const onLeft = useCallback(
    leftPress => {
      if (!leftPress || isKeypressDisabled) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return;
      }

      const newFocus = currentFocus - 1;
      timeoutRef.current = setTimeout(onLeftHold, 3000);

      if (currentFocus === min) {
        return;
      }

      setCurrentFocus(newFocus);
    },
    [currentFocus, onLeftHold, timeoutRef],
  );

  const onRightHold = useCallback(
    rightPressParam => {
      if ((!rightPress && rightPressParam === undefined) || rightPressParam === false) return;
      onSave?.(result);
    },
    [rightPress, result],
  );

  const onRight = useCallback(
    rightPressParam => {
      if (!rightPressParam || isKeypressDisabled) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return;
      }

      const newFocus = currentFocus + 1;
      timeoutRef.current = setTimeout(onRightHold, 3000);

      if (currentFocus === limit) {
        return;
      }
      setCurrentFocus(newFocus);
    },
    [currentFocus, timeoutRef, onRightHold],
  );

  useEffect(() => {
    onLeft(leftPress);
  }, [leftPress]);

  useEffect(() => {
    onRight(rightPress);
  }, [rightPress]);

  useEffect(() => {
    setTimeout(() => {
      onUpdateCommands(commands => ({...commands, onLeft, onRight, onLeftHold, onRightHold}));
    });
  }, [onLeft, onRight, onLeftHold, onRightHold]);

  useEffect(() => {
    if (!initiallyDisableKeypress) return;

    setTimeout(() => {
      setIsKeypressDisabled(false);
    }, 500);
  }, []);

  useEffect(() => {
    onChange(values);
  }, [values]);

  return (
    <>
      <div className={styles.content}>
        <div className={styles.top}>{caption}</div>
        <img className={styles.arrowLeft} src={ArrowLeft} alt="Left" />

        <div className={styles.border}>
          <TextNumbers
            style={currentFocus < 2 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 1}
            defaultKey={defaultKey1}
            chars={chars}
            onChange={value => setValues({...values, [1]: value})}
          />

          <TextNumbers
            style={currentFocus < 3 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 2}
            defaultKey={defaultKey2}
            chars={chars}
            onChange={value => setValues({...values, [2]: value})}
          />

          <TextNumbers
            style={currentFocus < 4 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 3}
            defaultKey={defaultKey3}
            chars={chars}
            onChange={value => setValues({...values, [3]: value})}
          />

          <TextNumbers
            style={currentFocus < 5 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 4}
            defaultKey={defaultKey4}
            chars={chars}
            onChange={value => setValues({...values, [4]: value})}
          />

          <TextNumbers
            style={currentFocus < 6 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 5}
            defaultKey={defaultKey5}
            chars={chars}
            onChange={value => setValues({...values, [5]: value})}
          />

          <TextNumbers
            style={currentFocus < 7 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 6}
            defaultKey={defaultKey6}
            chars={chars}
            onChange={value => setValues({...values, [6]: value})}
          />

          <TextNumbers
            style={currentFocus < 8 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 7}
            defaultKey={defaultKey7}
            chars={chars}
            onChange={value => setValues({...values, [7]: value})}
          />

          <TextNumbers
            style={currentFocus < 9 ? {display: 'flex'} : {display: 'none'}}
            isDisabled={currentFocus !== 8}
            defaultKey={defaultKey8}
            chars={chars}
            onChange={value => setValues({...values, [8]: value})}
          />

          <TextNumbers
            isDisabled={currentFocus !== 9}
            defaultKey={defaultKey9}
            chars={chars}
            onChange={value => setValues({...values, [9]: value})}
          />
          <TextNumbers
            isDisabled={currentFocus !== 10}
            defaultKey={defaultKey10}
            chars={chars}
            onChange={value => setValues({...values, [10]: value})}
          />
          <TextNumbers
            isDisabled={currentFocus !== 11}
            defaultKey={defaultKey11}
            chars={chars}
            onChange={value => setValues({...values, [11]: value})}
          />
          <TextNumbers
            isDisabled={currentFocus !== 12}
            defaultKey={defaultKey12}
            chars={chars}
            onChange={value => setValues({...values, [12]: value})}
          />
        </div>
        <img className={styles.arrowRight} src={ArrowRight} alt="Right" />
        <div className={styles.result}>{result.join('')}</div>
      </div>
    </>
  );
}

export default NumberView;
